import React, { useMemo } from 'react';
import { calculateGuillotineCutLines } from './BoardCanvas';

const PIECE_COLORS = [
  { fill: '#dbeafe', stroke: '#3b82f6', label: 'Azul' },
  { fill: '#d1fae5', stroke: '#10b981', label: 'Verde' },
  { fill: '#fef3c7', stroke: '#f59e0b', label: 'Amarelo' },
  { fill: '#ede9fe', stroke: '#8b5cf6', label: 'Roxo' },
  { fill: '#fce7f3', stroke: '#ec4899', label: 'Rosa' },
  { fill: '#e0e7ff', stroke: '#6366f1', label: 'Índigo' },
  { fill: '#ccfbf1', stroke: '#14b8a6', label: 'Teal' },
  { fill: '#fff7ed', stroke: '#f97316', label: 'Laranja' },
  { fill: '#f0fdf4', stroke: '#22c55e', label: 'Verde Claro' },
  { fill: '#fdf2f8', stroke: '#db2777', label: 'Pink' },
  { fill: '#eff6ff', stroke: '#2563eb', label: 'Azul Royale' },
  { fill: '#fefce8', stroke: '#ca8a04', label: 'Amarelo Ouro' },
];

const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

const getPieceColor = (pos) => {
  const key = `${pos.part_width || pos.width || 0}-${pos.part_height || pos.height || 0}-${pos.material_name || pos.material || ''}`;
  return PIECE_COLORS[hashCode(key) % PIECE_COLORS.length];
};

const BoardMiniCanvas = ({ board, positions = [], compact = false, onClick }) => {
  const boardWidth = parseFloat(board.width) || 2750;
  const boardHeight = parseFloat(board.height) || 1840;

  const CANVAS_W = 220;
  const CANVAS_H = Math.round((boardHeight / boardWidth) * CANVAS_W);
  const scale = CANVAS_W / boardWidth;

  const sorted = useMemo(() => {
    return [...positions].sort((a, b) => {
      const ay = parseFloat(a.position_y) || 0;
      const by = parseFloat(b.position_y) || 0;
      const ax = parseFloat(a.position_x) || 0;
      const bx = parseFloat(b.position_x) || 0;
      return ay - by || ax - bx;
    });
  }, [positions]);

  const areaUsed = useMemo(() => {
    return positions.reduce((sum, p) => {
      const pw = parseFloat(p.part_width || p.width || 0);
      const ph = parseFloat(p.part_height || p.height || 0);
      return sum + (pw * ph);
    }, 0) / 1000000;
  }, [positions]);

  const cutLines = useMemo(() => {
    return calculateGuillotineCutLines(positions, boardWidth, boardHeight);
  }, [positions, boardWidth, boardHeight]);

  const areaTotal = (boardWidth * boardHeight) / 1000000;
  const efficiency = areaTotal > 0 ? ((areaUsed / areaTotal) * 100).toFixed(1) : 0;

  return (
    <div
      onClick={onClick}
      className={`inline-flex flex-col items-center ${onClick ? 'cursor-pointer' : ''}`}
    >
      <svg
        width={CANVAS_W}
        height={CANVAS_H}
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        className="border border-gray-300 rounded bg-gray-50"
      >
        {sorted.map((pos, idx) => {
          const pw = parseFloat(pos.part_width || pos.width || 0);
          const ph = parseFloat(pos.part_height || pos.height || 0);
          const px = parseFloat(pos.position_x) || 0;
          const py = parseFloat(pos.position_y) || 0;
          const rot = pos.rotation || 'A';
          const sw = pw;
          const sh = ph;
          const c = getPieceColor(pos);

          return (
            <g key={pos.id || idx}>
              <rect
                x={px * scale}
                y={py * scale}
                width={sw * scale}
                height={sh * scale}
                fill={c.fill}
                stroke={c.stroke}
                strokeWidth={1}
                rx={1}
              />
              {!compact && sw * scale > 30 && sh * scale > 16 && (
                <>
                  <text
                    x={(px + sw / 2) * scale}
                    y={(py + sh / 2 - 3) * scale}
                    textAnchor="middle"
                    fontSize={Math.min(9, Math.max(6, sw * scale * 0.08))}
                    fill="#1f2937"
                    fontWeight="bold"
                  >
                    {pos.part_code?.length > 8
                      ? pos.part_code.substring(0, 8) + '..'
                      : pos.part_code || ''}
                  </text>
                  <text
                    x={(px + sw / 2) * scale}
                    y={(py + sh / 2 + 8) * scale}
                    textAnchor="middle"
                    fontSize={Math.min(7, Math.max(5, sw * scale * 0.06))}
                    fill="#4b5563"
                  >
                    {sw}x{sh}
                  </text>
                </>
              )}
            </g>
          );
        })}

        {/* Linhas de corte industrial guilhotina contínuas */}
        {cutLines.map((line, idx) => (
          <line
            key={`mini-cut-${idx}`}
            x1={line.x1 * scale}
            y1={line.y1 * scale}
            x2={line.x2 * scale}
            y2={line.y2 * scale}
            stroke={line.isPrimary ? "#dc2626" : "#0f172a"}
            strokeWidth={line.isPrimary ? 1.6 : 1.1}
            strokeLinecap="square"
          />
        ))}

        {positions.length === 0 && (
          <text
            x={CANVAS_W / 2}
            y={CANVAS_H / 2}
            textAnchor="middle"
            fontSize={10}
            fill="#9ca3af"
          >
            Vazio
          </text>
        )}
      </svg>

      {!compact && (
        <div className="w-full mt-1 text-[10px] text-gray-500 text-center leading-tight">
          <span className="font-semibold text-gray-700">{boardWidth}x{boardHeight}mm</span>
          {' '}{efficiency}%
        </div>
      )}
    </div>
  );
};

export default BoardMiniCanvas;
